import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Upload, 
  File, 
  Image, 
  Video, 
  Trash2, 
  Download, 
  Eye,
  X,
  FileText,
  MoreHorizontal,
  Printer,
  Mail
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

// File categories by entity type
const CATEGORY_OPTIONS = {
  child: [
    { value: 'referral', label: 'Referral Letter' },
    { value: 'medical_letter', label: 'Medical Letter' },
    { value: 'school_report', label: 'School Report' },
    { value: 'ehcp', label: 'EHCP Document' },
    { value: 'consent_form', label: 'Consent Form' },
    { value: 'child_photo', label: 'Child Photo' },
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'observation_video', label: 'Observation Video' },
    { value: 'external_assessment', label: 'External Assessment' },
    { value: 'other', label: 'Other' }
  ],
  staff: [
    { value: 'profile_photo', label: 'Profile Photo' },
    { value: 'passport', label: 'Passport' },
    { value: 'driving_licence', label: 'Driving Licence' },
    { value: 'visa_brp', label: 'Visa / BRP' },
    { value: 'dbs', label: 'DBS Certificate' },
    { value: 'professional_cert', label: 'Professional Certificate' },
    { value: 'educational_cert', label: 'Educational Certificate' },
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'contract', label: 'Contract' },
    { value: 'other', label: 'Other' }
  ],
  assessment: [
    { value: 'external_assessment', label: 'External Assessment' },
    { value: 'test_output', label: 'Test Output' },
    { value: 'clinical_photo', label: 'Clinical Photo' },
    { value: 'handwritten_note', label: 'Handwritten Note' },
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'other', label: 'Other' }
  ],
  evaluation: [
    { value: 'external_assessment', label: 'External Report' },
    { value: 'clinical_photo', label: 'Clinical Photo' },
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'other', label: 'Other' }
  ],
  progress_note: [
    { value: 'clinical_photo', label: 'Clinical Photo' },
    { value: 'handwritten_note', label: 'Handwritten Note' },
    { value: 'observation_video', label: 'Observation Video' },
    { value: 'other', label: 'Other' }
  ],
  policy: [
    { value: 'policy_document', label: 'Policy Document' },
    { value: 'other', label: 'Other' }
  ],
  event: [
    { value: 'event_photo', label: 'Event Photo' },
    { value: 'event_video', label: 'Event Video' },
    { value: 'other', label: 'Other' }
  ]
};

const ALLOWED_EXTENSIONS = {
  document: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.rtf'],
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'],
  video: ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.webm']
};

const ALL_EXTENSIONS = [...ALLOWED_EXTENSIONS.document, ...ALLOWED_EXTENSIONS.image, ...ALLOWED_EXTENSIONS.video];

// Progress Bar Component
function ProgressBar({ progress, fileName }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600 truncate max-w-[200px]">{fileName}</span>
        <span className="text-[#1e3a5f] font-medium">{progress}%</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-[#2a9d8f] transition-all duration-300 rounded-full"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// Drag and Drop Upload Zone
export function DragDropUploader({
  entityType,
  entityId,
  onUploadComplete,
  acceptTypes = 'all',
  multiple = true,
  title = 'Upload Files',
  description = 'Drag and drop files here or click to browse'
}) {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadForm, setUploadForm] = useState({ category: '', description: '' });

  const acceptedExtensions = acceptTypes === 'all' 
    ? ALL_EXTENSIONS.join(',')
    : acceptTypes === 'media' 
      ? [...ALLOWED_EXTENSIONS.image, ...ALLOWED_EXTENSIONS.video].join(',')
      : ALLOWED_EXTENSIONS[acceptTypes]?.join(',') || ALL_EXTENSIONS.join(',');

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(multiple ? files : [files[0]]);
      setDialogOpen(true);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(multiple ? files : [files[0]]);
      setDialogOpen(true);
    }
  };

  const uploadFile = async (file, index) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    formData.append('category', uploadForm.category);
    if (uploadForm.description) formData.append('description', uploadForm.description);

    try {
      await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [index]: progress }));
        }
      });
      return true;
    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      return false;
    }
  };

  const handleUploadAll = async () => {
    if (!uploadForm.category) {
      toast.error('Please select a category');
      return;
    }

    setUploadQueue(selectedFiles.map(f => f.name));
    
    let successCount = 0;
    for (let i = 0; i < selectedFiles.length; i++) {
      const success = await uploadFile(selectedFiles[i], i);
      if (success) successCount++;
    }

    toast.success(`${successCount}/${selectedFiles.length} files uploaded successfully`);
    setDialogOpen(false);
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadForm({ category: '', description: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onUploadComplete) onUploadComplete();
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const categories = CATEGORY_OPTIONS[entityType] || CATEGORY_OPTIONS.child;

  const getFileIcon = (file) => {
    const ext = file.name.toLowerCase().split('.').pop();
    if (ALLOWED_EXTENSIONS.image.some(e => e.includes(ext))) return <Image className="w-5 h-5 text-green-500" />;
    if (ALLOWED_EXTENSIONS.video.some(e => e.includes(ext))) return <Video className="w-5 h-5 text-purple-500" />;
    return <FileText className="w-5 h-5 text-blue-500" />;
  };

  return (
    <>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
          transition-all duration-200 ease-in-out
          ${isDragging 
            ? 'border-[#2a9d8f] bg-[#2a9d8f]/10 scale-[1.02]' 
            : 'border-slate-300 hover:border-[#1e3a5f] hover:bg-slate-50'
          }
        `}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={acceptedExtensions}
          multiple={multiple}
          className="hidden"
        />
        <div className="space-y-3">
          <div className={`
            w-16 h-16 mx-auto rounded-full flex items-center justify-center
            ${isDragging ? 'bg-[#2a9d8f]/20' : 'bg-slate-100'}
          `}>
            <Upload className={`w-8 h-8 ${isDragging ? 'text-[#2a9d8f]' : 'text-slate-400'}`} />
          </div>
          <div>
            <p className="font-medium text-slate-700">{title}</p>
            <p className="text-sm text-slate-500">{description}</p>
            <p className="text-xs text-slate-400 mt-1">
              {acceptTypes === 'media' ? 'Images & Videos' : acceptTypes === 'image' ? 'Images only' : 'PDF, DOCX, Images, Videos'}
            </p>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Upload {selectedFiles.length} File(s)</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File List */}
            <div className="max-h-40 overflow-y-auto space-y-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  {getFileIcon(file)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  {uploadProgress[index] !== undefined ? (
                    <div className="w-16">
                      <ProgressBar progress={uploadProgress[index]} fileName="" />
                    </div>
                  ) : (
                    <Button variant="ghost" size="icon" onClick={() => removeFile(index)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Category Selection */}
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea 
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Add a description..."
                rows={2}
              />
            </div>

            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  uploadProgress[index] !== undefined && (
                    <ProgressBar key={index} progress={uploadProgress[index]} fileName={file.name} />
                  )
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleUploadAll} 
              className="bg-[#1e3a5f]"
              disabled={!uploadForm.category || Object.keys(uploadProgress).length > 0}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function FileUploadButton({ 
  entityType, 
  entityId, 
  onUploadComplete,
  acceptTypes = 'all', // 'all', 'document', 'image', 'video'
  buttonText = 'Upload File',
  buttonVariant = 'outline',
  buttonSize = 'default',
  className = ''
}) {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    category: '',
    description: '',
    notes: ''
  });

  const acceptedExtensions = acceptTypes === 'all' 
    ? ALL_EXTENSIONS.join(',')
    : ALLOWED_EXTENSIONS[acceptTypes]?.join(',') || ALL_EXTENSIONS.join(',');

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.category) {
      toast.error('Please select a file and category');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      formData.append('category', uploadForm.category);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.notes) formData.append('notes', uploadForm.notes);

      const response = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      setDialogOpen(false);
      setSelectedFile(null);
      setUploadForm({ category: '', description: '', notes: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      if (onUploadComplete) onUploadComplete(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const categories = CATEGORY_OPTIONS[entityType] || CATEGORY_OPTIONS.child;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={acceptedExtensions}
        style={{ display: 'none' }}
      />
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => fileInputRef.current?.click()}
        className={className}
        data-testid="upload-file-btn"
      >
        <Upload className="w-4 h-4 mr-2" />
        {buttonText}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          
          {selectedFile && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-100 rounded-lg flex items-center gap-3">
                {selectedFile.type.startsWith('image/') ? (
                  <Image className="w-8 h-8 text-green-600" />
                ) : selectedFile.type.startsWith('video/') ? (
                  <Video className="w-8 h-8 text-purple-600" />
                ) : (
                  <FileText className="w-8 h-8 text-blue-600" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{selectedFile.name}</p>
                  <p className="text-sm text-slate-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedFile(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select 
                  value={uploadForm.category} 
                  onValueChange={(v) => setUploadForm({...uploadForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Brief description of the file..."
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={uploadForm.notes}
                  onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})}
                  placeholder="Any additional notes (e.g., expiry date, verification date)..."
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !selectedFile || !uploadForm.category}
              className="bg-[#1e3a5f]"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AttachmentsList({ 
  entityType, 
  entityId, 
  showUploadButton = true,
  title = 'Documents & Media',
  compact = false
}) {
  const { api, user } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await api.get(`/uploads/entity/${entityType}/${entityId}`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [api, entityType, entityId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await api.delete(`/uploads/${attachmentId}`);
      toast.success('File deleted');
      fetchAttachments();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await api.get(attachment.file_url, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handlePreview = async (attachment) => {
    try {
      const response = await api.get(attachment.file_url, { responseType: 'blob' });
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      
      // For images and videos, show in modal
      if (attachment.file_type === 'image' || attachment.file_type === 'video') {
        setPreviewUrl(url);
        setPreviewType(attachment.file_type);
      } 
      // For PDFs, open in a new window with proper handling
      else if (attachment.file_format === 'pdf' || contentType.includes('pdf')) {
        // Create a hidden iframe to display PDF (more reliable than window.open for blob URLs)
        const pdfWindow = window.open('', '_blank');
        if (pdfWindow) {
          pdfWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${attachment.original_filename || 'Document'}</title>
              <style>
                body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                iframe { width: 100%; height: 100%; border: none; }
              </style>
            </head>
            <body>
              <iframe src="${url}#toolbar=1&navpanes=1" type="application/pdf"></iframe>
            </body>
            </html>
          `);
          pdfWindow.document.close();
        } else {
          // Fallback: direct download if popup blocked
          toast.info('Popup blocked - downloading file instead');
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.original_filename;
          link.click();
        }
        // Clean up blob URL after delay
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
      // For Word docs and other documents, download directly (browsers can't display them)
      else {
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.original_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
        toast.info('Document downloaded');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview file. Please try downloading instead.');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-5 h-5 text-green-600" />;
      case 'video': return <Video className="w-5 h-5 text-purple-600" />;
      default: return <FileText className="w-5 h-5 text-blue-600" />;
    }
  };

  const getCategoryLabel = (category) => {
    const allCategories = Object.values(CATEGORY_OPTIONS).flat();
    const found = allCategories.find(c => c.value === category);
    return found?.label || category;
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">{title}</span>
          {showUploadButton && (
            <FileUploadButton
              entityType={entityType}
              entityId={entityId}
              onUploadComplete={fetchAttachments}
              buttonText="Upload"
              buttonSize="sm"
            />
          )}
        </div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading...</div>
        ) : attachments.length === 0 ? (
          <div className="text-sm text-slate-400">No files attached</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {attachments.map((att) => (
              <div 
                key={att.id} 
                className="flex items-center gap-2 px-2 py-1 bg-slate-100 rounded text-sm cursor-pointer hover:bg-slate-200"
                onClick={() => handlePreview(att)}
              >
                {getFileIcon(att.file_type)}
                <span className="max-w-[100px] truncate">{att.original_filename}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="card-icrcl">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-lg text-[#1e3a5f]">{title}</CardTitle>
          <CardDescription>Attached documents, images and videos</CardDescription>
        </div>
        {showUploadButton && (
          <FileUploadButton
            entityType={entityType}
            entityId={entityId}
            onUploadComplete={fetchAttachments}
          />
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <File className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>No files attached yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attachments.map((att) => (
              <div 
                key={att.id} 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {getFileIcon(att.file_type)}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{att.original_filename}</p>
                    <p className="text-xs text-slate-500">
                      {getCategoryLabel(att.category)} • {(att.file_size / 1024).toFixed(1)} KB • {new Date(att.uploaded_at).toLocaleDateString()}
                    </p>
                    {att.description && (
                      <p className="text-xs text-slate-600 mt-1">{att.description}</p>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handlePreview(att)}>
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDownload(att)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.print()}>
                      <Printer className="w-4 h-4 mr-2" />
                      Print
                    </DropdownMenuItem>
                    {(att.uploaded_by === user?.id || ['director', 'admin', 'hr'].includes(user?.role)) && (
                      <DropdownMenuItem onClick={() => handleDelete(att.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => { setPreviewUrl(null); setPreviewType(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            {previewType === 'image' && previewUrl && (
              <img src={previewUrl} alt="Preview" className="max-h-[70vh] object-contain" />
            )}
            {previewType === 'video' && previewUrl && (
              <video src={previewUrl} controls className="max-h-[70vh]" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function ProfileImageUpload({ staffId, currentImage, onUploadComplete }) {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'staff');
      formData.append('entity_id', staffId);
      formData.append('category', 'profile_photo');
      formData.append('description', 'Profile photo');

      const response = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Profile photo uploaded');
      if (onUploadComplete) onUploadComplete(response.data);
    } catch (error) {
      toast.error('Failed to upload photo');
      setPreview(currentImage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".jpg,.jpeg,.png,.webp"
        style={{ display: 'none' }}
      />
      <div 
        className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 border-2 border-slate-300"
        onClick={() => fileInputRef.current?.click()}
      >
        {preview ? (
          <img src={preview} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <Upload className="w-8 h-8 text-slate-400" />
        )}
      </div>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? 'Uploading...' : 'Change Photo'}
      </Button>
    </div>
  );
}

export default { FileUploadButton, AttachmentsList, ProfileImageUpload };
